import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AxiosResponse } from "axios";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { getRepromaterialOverviewReportTemplate } from "../../document-templates/RepromaterialOverviewReportTemplate";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { Product, ProductType } from "../../models/Product";
import { RepromaterialOverviewReport } from "../../models/Report";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { useApplicationContext } from "../../application/ApplicationContext";
import { getAllDocuments } from "../../services/NewVersionService";
import { Utility } from "../../core/Utility";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllProducts } from "../../services/ProductService";
import { getProductReservations } from "../../services/NewVersionService";
import "./RepromaterialOverviewReportPage.scss";

export const RepromaterialOverviewReportPage = () => {
  const { t } = useTranslation();

  const { state } = useApplicationContext();
  const businessYear = state.businessYear ?? new Date().getFullYear();
  const yearStart = new Date(businessYear, 0, 1);
  const yearEnd = new Date(businessYear, 11, 31);

  const getDefaultDateForBusinessYear = () => {
    const today = new Date();
    if (businessYear === today.getFullYear()) {
      return today;
    }
    // za “stare” poslovne godine – 31.12. te godine
    return new Date(businessYear, 11, 31);
  };
  const [date, setDate] = useState<Date>(getDefaultDateForBusinessYear());

  const [warehousesApi, setWarehousesApi] = useState<any[]>([]);
  const [productsApi, setProductsApi] = useState<Product[]>([]);
  const [productReservationsApi, setProductReservationsApi] = useState<any[]>(
    []
  );
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>("");
  const [repromaterialOverview, setRepromaterialOverview] = useState<
    RepromaterialOverviewReport[]
  >([]);

  const [changeState, setChangeState] = useState<boolean>(false);
  const [processing, setProcessing] = useState(false);

  const [pdfData, setPdfData] = useState<any>(
    getRepromaterialOverviewReportTemplate(
      repromaterialOverview,
      selectedWarehouse,
      date
    )
  );
  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const refreshLookups = async () => {
    const from = new Date(businessYear, 0, 1);
    const to = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate() + 1
    );
    const [wResp, pResp, rResp]: [AxiosResponse, AxiosResponse, AxiosResponse] =
      await Promise.all([
        getAllWarehouses(),
        getAllProducts(),
        getProductReservations(businessYear, from, to),
      ]);

    [wResp, pResp, rResp].forEach(ensureSuccess);

    const wh = wResp?.data?.result ?? [];
    const pr = pResp?.data?.result ?? [];
    const rr = rResp?.data?.result ?? [];

    setWarehousesApi(wh);
    setProductsApi(pr);
    setProductReservationsApi(rr);

    return { wh, pr, rr };
  };

  useEffect(() => {
    const run = async () => {
      setProcessing(true);
      try {
        const { wh, pr, rr } = await refreshLookups();
        await formatRepromaterialOverviewData(wh, pr, rr);
      } finally {
        setProcessing(false);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    //reset states on filter change
    setRepromaterialOverview([]);
    setChangeState((prev) => !prev);
  }, [selectedWarehouse, date]);

  useEffect(() => {
    const today = new Date();
    const newDate =
      businessYear === today.getFullYear()
        ? today
        : new Date(businessYear, 11, 31);

    setDate(newDate);
    setRepromaterialOverview([]);
    setChangeState((prev) => !prev);
  }, [businessYear]);

  useEffect(() => {
    const run = async () => {
      await formatRepromaterialOverviewData();
    };
    run();
  }, [changeState]);

  // populate pdf when data changes
  useEffect(() => {
    const newPdf = getRepromaterialOverviewReportTemplate(
      repromaterialOverview,
      (warehousesApi.find((item) => item.id === selectedWarehouse)
        ?.name as string) ?? " - Svi",
      date
    );
    setPdfData(newPdf);
  }, [repromaterialOverview]);

  const formatRepromaterialOverviewData = async (
    wh = warehousesApi,
    pr = productsApi,
    rr = productReservationsApi
  ) => {
    const warehouse = wh.find((item) => item.id === selectedWarehouse);
    const warehouseName = warehouse?.name ?? null;

    // Opseg: 1.1. poslovne godine – izabrani datum (uključivo)
    const from = new Date(businessYear, 0, 1);
    const to = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate() + 1
    ); // ekskluzivna gornja granica za backend
    const dateToFormated = to.getTime(); // koristimo za rezervacije

    let copyOfRepromaterialOverview: RepromaterialOverviewReport[] = [];

    // 1) Povuci prijemnice i otpremnice repromaterijala preko novog API-ja
    const [inputsResp, outputsResp]: [AxiosResponse, AxiosResponse] =
      await Promise.all([
        getAllDocuments(false, true, from, to), // repromaterijal - prijemnice
        getAllDocuments(false, false, from, to), // repromaterijal - otpremnice
      ]);

    const repromaterialInputs = inputsResp.data?.result ?? [];
    const repromaterialOutputs = outputsResp.data?.result ?? [];

    const matchesWarehouse = (doc: any) => {
      if (warehouseName && doc.warehouseName !== warehouseName) return false;
      return true;
    };

    const accumulateReproFromDoc = (
      doc: any,
      sign: 1 | -1, // 1 = ulaz, -1 = izlaz
      target: RepromaterialOverviewReport[]
    ) => {
      if (doc?.isDocumentFinished !== true) return;
      if (!matchesWarehouse(doc)) return;

      const productIds = (doc.reproProducts ?? "")
        .split(";")
        .map((x: string) => x.trim())
        .filter((x: string) => x !== "");

      const amounts = (doc.reproAmounts ?? "")
        .split(";")
        .map((x: string) => x.trim())
        .filter((x: string) => x !== "");

      productIds.forEach((prodId: string, idx: number) => {
        const rawAmount = amounts[idx] ?? "0";
        const parsed = parseFloat(rawAmount);
        const amount = Math.abs(isNaN(parsed) ? 0 : parsed);
        if (!amount) return;

        const productInfo = pr.find(
          (p) => p.id?.toString() === prodId.toString()
        );
        if (!productInfo) return;

        const existingIndex = target.findIndex(
          (r) => r.product.id?.toString() === prodId.toString()
        );

        if (existingIndex >= 0) {
          const row = target[existingIndex];
          if (sign === 1) {
            // ULAZ
            row.amountInput += amount;
            row.amount += amount;
            row.amountWithReservation += amount;
          } else {
            // IZLAZ
            row.amountOutput += amount;
            row.amount -= amount;
            row.amountWithReservation -= amount;
          }
        } else {
          if (sign === 1) {
            // novi proizvod – ulaz
            target.push({
              id: productInfo.id,
              code: productInfo.code as string,
              name: productInfo.name as string,
              product: {
                id: productInfo.id,
                measuringUnit: productInfo.measuringUnit,
                amount: amount as any,
              } as Product,
              mark: productInfo.measuringUnit as string,
              amountInput: amount,
              amountOutput: 0,
              amount: amount,
              amountReserved: 0,
              amountWithReservation: amount,
            });
          } else {
            // novi proizvod – izlaz
            target.push({
              id: productInfo.id,
              code: productInfo.code as string,
              name: productInfo.name as string,
              product: {
                id: productInfo.id,
                measuringUnit: productInfo.measuringUnit,
                amount: amount as any,
              } as Product,
              mark: productInfo.measuringUnit as string,
              amountInput: 0,
              amountOutput: amount,
              amount: -amount,
              amountReserved: 0,
              amountWithReservation: -amount,
            });
          }
        }
      });
    };

    // 2) ULAZ REPROMATERIJALA
    for (const doc of repromaterialInputs) {
      accumulateReproFromDoc(doc, 1, copyOfRepromaterialOverview);
    }

    // 3) IZLAZ REPROMATERIJALA
    for (const doc of repromaterialOutputs) {
      accumulateReproFromDoc(doc, -1, copyOfRepromaterialOverview);
    }

    // 4) REZERVACIJE – ostaje ista logika, samo koristimo dateToFormated
    for (const productReservation of rr) {
      if (
        productReservation.type === ProductType.RawMaterial &&
        (warehouseName
          ? productReservation.warehouse?.name === warehouseName
          : true) &&
        new Date(productReservation?.expiryDate as Date).getTime() >=
          dateToFormated // rezervacija važi posle izabranog datuma
      ) {
        const reservedProducts = JSON.parse(
          productReservation.reproProductsRemainingReservationJSON as string
        ) as Product[];

        for (const repromaterialProduct of reservedProducts) {
          const reservedAmount = parseFloat(
            (repromaterialProduct?.amount ?? 0).toString()
          );
          if (!reservedAmount) continue;

          const productInfo = pr.find((p) => p.id == repromaterialProduct.id);
          if (!productInfo) continue;

          const index = copyOfRepromaterialOverview.findIndex(
            (repromaterial) =>
              repromaterial.product.id === repromaterialProduct.id
          );

          if (index >= 0) {
            copyOfRepromaterialOverview[index].amountReserved += reservedAmount;
            copyOfRepromaterialOverview[index].amountWithReservation -=
              reservedAmount;
          } else {
            copyOfRepromaterialOverview.push({
              id: repromaterialProduct.id,
              code: productInfo.code as string,
              name: productInfo.name as string,
              product: repromaterialProduct as Product,
              mark: productInfo.measuringUnit as string,
              amountInput: 0,
              amountOutput: 0,
              amount: 0,
              amountReserved: reservedAmount,
              amountWithReservation: -reservedAmount,
            });
          }
        }
      }
    }

    const filteredCopyOfRepromaterialOverview =
      copyOfRepromaterialOverview.filter(
        (oneReprom) => oneReprom.code !== "" && oneReprom.code !== undefined
      );
    const sortedFilteredCopyOfRepromaterial =
      filteredCopyOfRepromaterialOverview.sort(
        (a: RepromaterialOverviewReport, b: RepromaterialOverviewReport) =>
          a?.name > b?.name ? 1 : -1
      );

    setRepromaterialOverview(sortedFilteredCopyOfRepromaterial);
  };

  const onSelectedWarehouseChange = (property: string, value: string) => {
    setSelectedWarehouse(value);
  };

  const onDateChange = (property: string, value: Date | null) => {
    setDate(value as Date);
  };

  const renderRepromaterialHeader = () => {
    return (
      <div className="repromaterial-header">
        <span>{t("REPROMATERIJAL")}</span>
      </div>
    );
  };

  const categoryOfProduct = (rowData: any) => {
    return productsApi.find((item) => item.id === rowData.product.id)?.category
      ?.name;
  };

  const formatMark = (rowData: any) => {
    return productsApi.find((item) => item.id === rowData.product.id)
      ?.measuringUnit;
  };

  const productInput = (rowData: any) => {
    return !Number.isInteger(rowData.amountInput)
      ? rowData.amountInput.toFixed(3)
      : rowData.amountInput.toFixed(0);
  };

  const productOutput = (rowData: any) => {
    return !Number.isInteger(rowData.amountOutput)
      ? rowData.amountOutput.toFixed(3)
      : rowData.amountOutput.toFixed(0);
  };

  const productState = (rowData: any) => {
    return !Number.isInteger(rowData.amount)
      ? rowData.amount.toFixed(3) == 0 ||
        rowData.amount.toFixed(3) == 0.001 ||
        rowData.amount.toFixed(3) == -0.001
        ? "0"
        : rowData.amount.toFixed(3)
      : rowData.amount.toFixed(0);
  };

  const productReservation = (rowData: any) => {
    return !Number.isInteger(rowData.amountReserved)
      ? rowData.amountReserved.toFixed(3)
      : rowData.amountReserved.toFixed(0);
  };

  const productStateWithReservation = (rowData: any) => {
    return !Number.isInteger(rowData.amountWithReservation)
      ? rowData.amountWithReservation.toFixed(3) == 0 ||
        rowData.amountWithReservation.toFixed(3) == 0.001 ||
        rowData.amountWithReservation.toFixed(3) == -0.001
        ? "0"
        : rowData.amountWithReservation.toFixed(3)
      : rowData.amountWithReservation.toFixed(0);
  };

  const {
    control,
    formState: { errors },
  } = useForm({
    mode: "all",
  });

  const exportToExcel = () => {
    const excelRepromaterialObject = repromaterialOverview.map(
      (item: RepromaterialOverviewReport) => {
        return {
          SifraRobe: item?.code ?? "",
          Naziv: item?.name ?? "",
          Vrsta:
            productsApi.find(
              (oneProduct) => oneProduct.id === item?.product?.id
            )?.category?.name ?? "",
          Oznaka:
            productsApi.find(
              (oneProduct) => oneProduct.id === item?.product?.id
            )?.measuringUnit ?? "",
          UlazRobe: item.amountInput ?? "",
          IzlazRobe: item.amountOutput ?? "",
          StanjeRobe: item.amount ?? "",
          Rezervisano: item.amountReserved ?? "",
          StanjeSaRezervacijom: item.amountWithReservation ?? "",
        };
      }
    );
    const excelObject = [
      {
        SifraRobe: "",
        Naziv: "",
        Vrsta: "",
        Oznaka: "",
        UlazRobe: "",
        IzlazRobe: "",
        StanjeRobe: "",
        Rezervisano: "",
        StanjeSaRezervacijom: "",
      },
      ...excelRepromaterialObject,
    ];
    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(excelObject);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      saveAsExcelFile(
        excelBuffer,
        `Stanje repromaterijala na dan ${Utility.formatDate(new Date())}`
      );
    });
  };

  const saveAsExcelFile = (buffer: any, fileName: any) => {
    import("file-saver").then((module: any) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });
        module.default.saveAs(data, fileName + EXCEL_EXTENSION);
      }
    });
  };

  return (
    <PageLayout>
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-shop-window header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
              }}
            ></i>
            <span className="header-title">
              {t("Stanje repromaterijala na dan")}
            </span>
            <DateInput
              id="date"
              value={date}
              onValueChange={onDateChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements">{t("u magacinu")}</span>
            <DropdownInput
              id="selectedWarehouse"
              value={selectedWarehouse}
              onValueChange={onSelectedWarehouseChange}
              options={warehousesApi.filter((item) => item.virtual !== true)}
              optionValue="id"
              optionLabel="name"
              labelText=""
              placeholderText={t("Magacin")}
              control={control}
              errors={errors}
              disabled={processing}
            />
          </div>
          <div className="header-right">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-pdf"
              onButtonClick={() => {
                if (downloadPdfDocument.current) {
                  // @ts-ignore
                  downloadPdfDocument.current();
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-printer"
              onButtonClick={() => {
                if (printPdfDocument.current) {
                  // @ts-ignore
                  printPdfDocument.current();
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                marginLeft: "5px",
              }}
              labelText={""}
              icon="bi bi-file-earmark-excel"
              onButtonClick={exportToExcel}
            />
          </div>
        </div>
        <div className="table-body-repromaterial">
          {processing ? (
            <ProgressSpinner
              style={{ width: "100px", height: "100px", color: "green" }}
              strokeWidth="4"
              animationDuration=".8s"
            />
          ) : (
            <DataTable
              header={renderRepromaterialHeader}
              value={repromaterialOverview}
              showGridlines
              stripedRows
            >
              <Column field="code" header={t("Šifra robe")} />
              <Column field="name" header={t("Naziv proizvoda")} />
              <Column body={categoryOfProduct} header={t("Vrsta robe")} />
              <Column body={formatMark} header={t("Oznaka")} />
              <Column body={productInput} header={t("Ulaz robe")} />
              <Column body={productOutput} header={t("Izlaz robe")} />
              <Column body={productState} header={t("Stanje robe")} />
              <Column body={productReservation} header={t("Rezervisano")} />
              <Column
                body={productStateWithReservation}
                header={t("Stanje sa rezervacijom")}
              />
            </DataTable>
          )}
        </div>
        <div className="document-preview">
          <PdfPreview
            pdfDefinition={pdfData}
            downloadDocument={downloadPdfDocument}
            printDocument={printPdfDocument}
            documentName={`${t("Stanje repromaterijala u magacinu ")}${
              warehousesApi.find((item) => item.id === selectedWarehouse)
                ?.name ?? ""
            }_${Utility.formatDate(new Date())}.pdf`}
          />
        </div>
      </div>
    </PageLayout>
  );
};
